import clsx from 'clsx';
import { Link } from 'react-router-dom';

import { Icon, Typography } from '@/ui';

import type { MomentsMemberArtifactSummary } from '@/store/api/momentsApi';
import type { FC, MouseEvent } from 'react';

import {
  getSessionReflectionPath,
  getSessionReflectionSnippet,
} from '@/components/Home/SessionReflections/sessionReflectionHomeUtils';

interface SessionReflectionChipProps {
  artifact: MomentsMemberArtifactSummary;
  className?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * Reflection row on a past-session card, per the Figma "Past Sessions" frame
 * (17420:41868): sage-100 fill, leaf icon left, one-line snippet, chevron right.
 *
 * The chip must stay `w-full` with a `min-w-0` snippet: `truncate` only clips
 * when the box is width-constrained, so an `inline-flex` chip sized to its own
 * text grows the row instead of ellipsizing and crowds the session actions
 * menu. Card call sites give the surrounding column `min-w-0 flex-1` for the
 * same reason.
 *
 * Shared by both past-session surfaces (`Sessions/Card` and
 * `Sessions/Member/MemberSessionCard`) so the styling cannot drift again.
 */
const SessionReflectionChip: FC<SessionReflectionChipProps> = ({
  artifact,
  className = undefined,
  onClick = undefined,
}) => (
  <Link
    className={clsx(
      'flex w-full items-center gap-2 rounded-lg bg-sage-100 px-3 py-2 no-underline transition hover:bg-sage-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-500',
      className,
    )}
    to={getSessionReflectionPath(artifact)}
    onClick={onClick}
  >
    <Icon className="shrink-0 text-sage-500" aria-hidden>
      psychiatry
    </Icon>
    {/* font-medium / tracking match the design's typography/body2 (Medium 500,
        0.17px letter-spacing); this theme's body2 token is 400 / 0px. */}
    <Typography
      className="min-w-0 flex-1 truncate font-medium tracking-[0.17px] text-cement-500"
      variant="body2"
    >
      {getSessionReflectionSnippet(artifact.markdown, artifact.profileSlug)}
    </Typography>
    <Icon className="shrink-0 text-cement-500" fontSize="small" aria-hidden>
      chevron_right
    </Icon>
  </Link>
);

export default SessionReflectionChip;
